import React from 'react';
import { AxiosError, AxiosResponse } from 'axios';
import { Autocomplete } from '@material-ui/lab';
import { Button, Grid, Icon, Link, Typography } from '@material-ui/core';
import { Table, TableContainer, TableHead, TableRow, TableBody, TableCell, TextField, Paper, DialogContentText } from '@material-ui/core';

import { NewsSpreadsheetUploadModel, newsSpreadsheetUploadDataInitial } from '../../../Models/NewsSpreadsheetUploadModel';
import ClientService from '../../../Services/ClientService';
import { ClientModel } from '../../../Models/ClientModel';
import { BasicDropzone } from '../../../shared/components/BasicDropzone';
import { ClipperModel } from '../../../Models/ClipperModel';
import ClipperService from '../../../Services/ClipperService';
import { BasicDialog } from '../../../shared/components/BasicDialog';
import { AuthContext } from '../../Authentication/AuthContext';
import { useInputStyles } from '../../../shared/components/UploadStyles';
import { HomeContext } from '../../Home/HomeContext';

interface UploadInputProps {
    changeUpload: (data: NewsSpreadsheetUploadModel, file: File) => void,
}

export function NewsSpreadsheetUploadInput({ changeUpload }: UploadInputProps) {
    const classes = useInputStyles();

    const authContext = React.useContext(AuthContext);
    const homeContext = React.useContext(HomeContext);

    const [uploadData, setUploadData] = React.useState(newsSpreadsheetUploadDataInitial);
    const [file, setFile] = React.useState<File | null>(null);
    const [clientSelect, setClientSelect] = React.useState<ClientModel | null>(null);
    const [listClients, setListClients] = React.useState([]);
    const [clipperSelect, setClipperSelect] = React.useState<ClipperModel | null>(null);
    const [listClippers, setListClippers] = React.useState([]);
    const [openDetailClipper, setOpenDetailClipper] = React.useState(false);


    React.useEffect(() => {
        ClientService.clientByUserService(authContext.login.user.id)
            .getAll()
            .then((res: AxiosResponse) => {
                setListClients(res.data);

                if (res.data.length === 1)
                    setClientSelect(res.data[0]);
                else
                    setClientSelect(itemById(uploadData.clientId ?? 0, res.data));
            })
            .catch((error: AxiosError) => { homeContext.showError('', error); });
        ClipperService.clipperService()
            .getAll()
            .then((res: AxiosResponse) => { setListClippers(res.data); })
            .catch((error: AxiosError) => { homeContext.showError('', error); });
    }, []);

    React.useEffect(() => {
        if (clientSelect && clientSelect.id)
            setUploadData({ ...uploadData, clientId: clientSelect?.id });
    }, [clientSelect]);

    function itemById(id: number, list: ClientModel[]) {
        const result = list.find((item: ClientModel) => item.id === id);
        return result ?? null;
    }

    const rows = [
        { data: 'ID da notícia', column: clipperSelect?.columnIdNews || '-' },
        { data: 'Título', column: clipperSelect?.columnTitle || '-' },
        { data: 'Data de Publicação', column: clipperSelect?.columnPublishDate || '-' },
        { data: 'Jornalista', column: clipperSelect?.columnJournalist || '-' },
        { data: 'Link da notícia da clipadora', column: clipperSelect?.columnClipperLink || '-' },
        { data: 'Link da notícia original', column: clipperSelect?.columnNewsLink || '-' },
        { data: 'Veículo', column: clipperSelect?.columnPublicationPlace || '-' },
        { data: 'Mídia', column: clipperSelect?.columnMedia || '-' },
        { data: 'Centimetragem', column: clipperSelect?.columnCentimeter || '-' },
    ];

    function detailClipper() {
        return (
            <>
                <DialogContentText>
                    <TableContainer>
                        <Paper elevation={0}>
                            <Table
                                size="small"
                                aria-label="a dense table">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Dado</TableCell>
                                        <TableCell align="center">Coluna</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {rows.map((row) => (
                                        <TableRow key={row.data}>
                                            <TableCell component="th" scope="row">
                                                {row.data}
                                            </TableCell>
                                            <TableCell align="center">
                                                {row.column}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Paper>
                    </TableContainer>
                </DialogContentText>
                <DialogContentText>
                    <Typography
                        variant="body2"
                        className={classes.infoText}>
                        * A primeira linha de dados deverá ser a linha 1, obrigatoriamente.
                    </Typography>
                </DialogContentText>
            </>
        );
    }

    function getDisabledSubmit() {
        return (clientSelect == null) || (clipperSelect == null) || (file == null);
    }

    return (
        <>
            <Grid
                container
                direction="column"
                justify="center"
                alignItems="center">
                <Typography variant="h3" className={classes.title}>
                    Insira os dados solicitados
                </Typography>
                <Typography variant="body1" className={classes.item}>
                    {'Insira uma planilha que contenha pelo menos 5 colunas e que esteja no formato .xls ou .xlsx. ' +
                        'Indique nos campos solicitados qual é o cliente e clipadora. ' +
                        'Lembre-se de ordenar as linhas da planilha na sequência que deseja realizar a classificação.'}
                </Typography>
                <Autocomplete
                    id="combo-box-clients"
                    value={clientSelect}
                    onChange={(_, newValue: ClientModel | null) => { setClientSelect(newValue); }}
                    options={listClients}
                    getOptionLabel={(option: ClientModel) => option.name}
                    className={classes.fields}
                    noOptionsText="Sem resultados"
                    loadingText="Carregando..."
                    renderInput={(params) =>
                        <TextField
                            {...params}
                            label="Cliente"
                            variant="outlined" />
                    } />
                <Autocomplete
                    id="combo-box-clippers"
                    value={clipperSelect}
                    onChange={(_, newValue: ClipperModel | null) => {
                        setClipperSelect(newValue);
                        if (newValue && newValue.id)
                            setUploadData({ ...uploadData, clipperId: newValue?.id });
                    }}
                    options={listClippers}
                    getOptionLabel={(option: ClipperModel) => option.name}
                    className={classes.fields}
                    noOptionsText="Sem resultados"
                    loadingText="Carregando..."
                    renderInput={(params) =>
                        <TextField
                            {...params}
                            label="Clipadora"
                            variant="outlined" />
                    } />
                <Link href="#" className={classes.textLink} onClick={() => { if (clipperSelect) setOpenDetailClipper(true); }}>
                    <Icon>help_outline</Icon>
                    <Typography variant="caption" className={classes.itemDetail}>
                        Ver configurações de campos da planilha
                    </Typography>
                </Link>
                <BasicDropzone onChangefile={(file: File) => setFile(file)} isClipperSpreadsheetUpload={true} />
                <Button
                    size="large"
                    variant="contained"
                    color="primary"
                    className={classes.fields}
                    disabled={getDisabledSubmit()}
                    onClick={() => { if (file) { changeUpload(uploadData, file); setUploadData(newsSpreadsheetUploadDataInitial); } }}>
                    Próximo
                </Button>
                {clipperSelect && (
                    <BasicDialog
                        open={openDetailClipper}
                        handleClose={() => setOpenDetailClipper(false)}
                        title={clipperSelect?.name || ''}
                        description="A relação de campos da planilha a ser inserida na plataforma deve seguir o padrão mostrado abaixo."
                        children={detailClipper()} />
                )}
            </Grid>
        </>
    );
}